import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, RefreshCw, Hotel } from 'lucide-react';
import { PilgrimsTable } from '../components/pilgrims/PilgrimsTable';
import { UploadPilgrimsModal } from '../components/pilgrims/UploadPilgrimsModal';
import { UpdatePilgrimsModal } from '../components/pilgrims/UpdatePilgrimsModal';
import { generateExcelTemplate } from '../utils/pilgrimValidation';
import { PilgrimSchema } from '../types/pilgrim';
import { usePilgrimsStore } from '../stores/pilgrimsStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { canUploadPilgrims } from '../utils/roleChecks';


export const PilgrimsPage = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { translations } = useLanguage();
  const { 
    pilgrims, 
    loading, 
    error, 
    initialized, 
    initializeListener, 
    uploadPilgrims,
    updatePilgrims,
    deleteAll 
  } = usePilgrimsStore();

  useEffect(() => {
    if (!initialized) {
      initializeListener();
    }
  }, [initialized, initializeListener]);

  const handleDownloadTemplate = () => {
    const template = generateExcelTemplate();
    const ws = XLSX.utils.json_to_sheet([template]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "pilgrims-template.xlsx");
  };

  const handleUpload = async (data: PilgrimSchema[]) => {
    try {
      const result = await uploadPilgrims(data);
      return { message: result.message };
    } catch (error: any) {
      throw new Error(error.message || translations?.pilgrimsPage?.uploadError || 'An error occurred while uploading data');
    }
  };

  const handleUpdate = async (data: PilgrimSchema[]) => {
    try {
      const result = await updatePilgrims(data);
      return { message: result.message };
    } catch (error: any) {
      throw new Error(error.message || translations?.pilgrimsPage?.updateError || 'An error occurred while updating data');
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAll();
      toast.success(translations?.pilgrimsPage?.deleteSuccess || 'All pilgrims have been deleted successfully');
    } catch (error: any) {
      toast.error(error.message || translations?.pilgrimsPage?.deleteError || 'An error occurred while deleting pilgrims');
    }
    setIsDeleteConfirmOpen(false);
  };

  const handleSyncRoomTypes = async () => {
    try {
      setIsSyncing(true);
      console.log('Starting optimized room types sync process...');

      // Get all links first
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksSnapshot = await getDocs(linksRef);
      
      if (linksSnapshot.empty) {
        throw new Error('No links found');
      }

      console.log(`Found ${linksSnapshot.size} links to process`);

      // Create a map of pilgrimId -> link document reference
      const linkMap = new Map();
      linksSnapshot.docs.forEach(doc => {
        linkMap.set(doc.data().pilgrimId, {
          ref: doc.ref,
          data: doc.data()
        });
      });

      // Get all pilgrims that have links
      const pilgrimIds = Array.from(linkMap.keys());
      const pilgrimBatches = [];
      const BATCH_SIZE = 25; // Reduced to stay under Firestore's 30 item limit

      // Split pilgrim IDs into batches
      for (let i = 0; i < pilgrimIds.length; i += BATCH_SIZE) {
        pilgrimBatches.push(pilgrimIds.slice(i, i + BATCH_SIZE));
      }

      console.log(`Split into ${pilgrimBatches.length} batches of ${BATCH_SIZE}`);

      let successCount = 0;
      let errorCount = 0;

      // Initialize write batch
      let batch = writeBatch(firestore);
      let batchOperations = 0;

      // Process each batch
      for (let i = 0; i < pilgrimBatches.length; i++) {
        const currentBatch = pilgrimBatches[i];
        console.log(`Processing batch ${i + 1}/${pilgrimBatches.length}`);

        // Get pilgrims data for this batch
        const pilgrimsRef = collection(firestore, 'Pilgrims');
        const q = query(pilgrimsRef, where('__name__', 'in', currentBatch));
        const pilgrimsSnapshot = await getDocs(q);

        // Process each pilgrim in the batch
        for (const pilgrimDoc of pilgrimsSnapshot.docs) {
          const pilgrimData = pilgrimDoc.data();
          const linkInfo = linkMap.get(pilgrimDoc.id);

          if (!linkInfo) continue;

          try {
            // Update link with room types
            batch.update(linkInfo.ref, {
              main_makkah_roomType: pilgrimData.main_makkah_roomType || '',
              shifting_makkah_roomType: pilgrimData.shifting_makkah_roomType || '',
              madinah_roomType: pilgrimData.madinah_roomType || '',
              updatedAt: Date.now()
            });

            batchOperations++;
            successCount++;
          } catch (error) {
            console.error(`Error processing pilgrim ${pilgrimDoc.id}:`, error);
            errorCount++;
          }

          // Commit batch if it reaches the limit
          if (batchOperations >= 450) { // Firestore limit is 500
            await batch.commit();
            console.log(`Committed batch with ${batchOperations} operations`);
            batch = writeBatch(firestore);
            batchOperations = 0;
          }
        }

        console.log(`Batch ${i + 1} complete. Progress: ${successCount} successful, ${errorCount} failed`);
      }

      // Commit any remaining operations in the final batch
      if (batchOperations > 0) {
        await batch.commit();
        console.log(`Committed final batch with ${batchOperations} operations`);
      }

      console.log('Sync completed:', {
        successful: successCount,
        failed: errorCount
      });

      toast.success(`تم تحديث نوع الغرف لـ ${successCount} حاج بنجاح${errorCount > 0 ? ` (${errorCount} فشل)` : ''}`);
    } catch (error) {
      console.error('Error syncing room types:', error);
      toast.error('فشل في تحديث نوع الغرف');
    } finally {
      setIsSyncing(false);
    }
  };

  if (loading && !initialized) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {translations?.pilgrimsPage?.header || 'Pilgrims Information'}
        </h1>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadTemplate}
            className="bg-primary-50 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-100 
                     transition-colors flex items-center gap-2"
          >
            <Download size={20} />
            <span>{translations?.pilgrimsPage?.downloadTemplate || 'Upload Template Example'}</span>
          </button>
          {canUploadPilgrims() && (
            <>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600
                         transition-colors flex items-center gap-2"
              >
                <Upload size={20} />
                <span>{translations?.pilgrimsPage?.addPilgrims || 'Add Pilgrims'}</span>
              </button>
              <button
                onClick={() => setIsUpdateModalOpen(true)}
                className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600
                         transition-colors flex items-center gap-2"
              >
                <RefreshCw size={20} />
                <span>{translations?.pilgrimsPage?.updatePilgrims || 'Update Pilgrims'}</span>
              </button>
            </>
          )}
          <button
            onClick={handleSyncRoomTypes}
            disabled={isSyncing}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 
                     transition-colors flex items-center gap-2"
          >
            <Hotel size={20} className={isSyncing ? 'animate-spin' : ''} />
            <span>{isSyncing ? 'جاري المزامنة...' : 'مزامنة نوع الغرف'}</span>
          </button>
          <button
            onClick={() => setIsDeleteConfirmOpen(true)}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 
                     transition-colors flex items-center gap-2"
          >
            <Trash2 size={20} />
            <span>{translations?.pilgrimsPage?.deleteAll || 'Delete All Pilgrims'}</span>
          </button>
        </div>
      </div>

      <PilgrimsTable pilgrims={Object.values(pilgrims)} />

      <UploadPilgrimsModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />

      <UpdatePilgrimsModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onUpdate={handleUpdate}
      />

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteAll}
        title={translations?.pilgrimsPage?.deleteAllTitle || 'Delete All Pilgrims'}
        message={translations?.pilgrimsPage?.deleteAllMessage || 'Are you sure you want to delete all pilgrims? This action cannot be undone.'}
        type="danger"
        confirmText={translations?.pilgrimsPage?.confirmDelete || 'Delete All'}
        cancelText={translations?.pilgrimsPage?.cancel || 'Cancel'}
      />
    </div>
  );
};